import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { afterRender } from '../../../../common/after-render';
import { isEmpty, isStringEmpty, isStringEqual } from '../../../../common/functions';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';
import { IUnitOfMeasure } from '../../../../services/insecta';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-substrate-item',
  standalone: true,
  imports: [
    SelectModule,
    CommonModule,
    FormsModule,
    ToggleSwitchModule,
    InputNumberModule,
    FloatLabelModule,
    CardModule,
    LockInitialWidthDirective,
    TooltipModule
  ],
  templateUrl: './substrate-item.component.html'
})
export class SubstrateItemComponent implements OnChanges {
  @ViewChild('substrateSelect', { read: ElementRef }) substrateSelect!: ElementRef;

  @Input() catalogInformation: any[] = [];
  @Input() filteredSubstrates: any[] = [];
  @Input() selectedContainerId: string | null = null;
  @Input() units: IUnitOfMeasure[] = [];
  @Input() selectedContainerTypeName: string = 'container';
  @Input() isDisabled: boolean = false;
  @Input() reset: boolean = false;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() editingOrderItemSubstrate: any;
  @Output() substrateItemChangeEvent = new EventEmitter<any>();
  standardTooltipText: string =
    'By default, the insectary team will use their standard process to fill your order. If you would like to customize your request, turn off the "Standard" and enter your custom amount below.';
  substrates: any[] = [];
  substrateItem = {
    catalogContainerSubstrateId: null,
    substrateId: null,
    standard: true,
    customAmount: null,
    customUnit: null
  };

  standardToggleDisabled: boolean = true;
  allCatalogSubstrates: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).every((change) => change.firstChange)) {
      return;
    }

    if (this.reset) {
      this.resetSubstrateItem();
      this.standardToggleDisabled = true;
      afterRender(() => this.emitSubstrateItem());
      return;
    }

    if (this.selectedContainerId == null) {
      this.substrateItem.substrateId = null;
    }

    if ('filteredSubstrates' in changes && 'selectedContainerId' in changes) {
      const prevContId = changes['selectedContainerId'].previousValue;
      const currContId = changes['selectedContainerId'].currentValue;
      if (isEqual(prevContId, currContId)) {
        return;
      }

      if (isEmpty(this.substrateItem.substrateId)) {
        this.resetSubstrateItem();
        this.standardToggleDisabled = true;
        setTimeout(() => this.emitSubstrateItem(), 0);
      }

      if (!isEmpty(this.substrateItem.substrateId) && !isEmpty(this.selectedContainerId)) {
        const substrateSelected = (this.catalogInformation ?? [])
          .flatMap((ci) => ci?.Substrates ?? [])
          .find(
            (sub) => isStringEqual(sub.SubstrateId, this.substrateItem.substrateId!) && isStringEqual(sub.ContainerId, this.selectedContainerId!)
          );

        this.variableUpdates(substrateSelected);
        this.standardToggleDisabled = false;
        setTimeout(() => this.emitSubstrateItem(), 0);
      }
    }

    if (changes['editingOrderItemSubstrate']) {
      if (!isStringEmpty(this.editingOrderItemSubstrate.catalogContainerSubstrateId)) {
        const substrateSelected = this.catalogInformation
          .flatMap((info: any) => info.Substrates)
          .find((substrates: any) =>
            isStringEqual(substrates.CatalogContainerSubstrateId, this.editingOrderItemSubstrate.catalogContainerSubstrateId)
          );
        this.substrateItem.catalogContainerSubstrateId = substrateSelected?.CatalogContainerSubstrateId;
        this.substrateItem.substrateId = substrateSelected?.SubstrateId;
        this.substrateItem.standard = this.editingOrderItemSubstrate.standardFillAmount;
        this.substrateItem.customAmount = this.editingOrderItemSubstrate.substrateAmount;
        this.substrateItem.customUnit = this.editingOrderItemSubstrate.substrateAmountUom;
        this.standardToggleDisabled = false;

        if (substrateSelected) {
          this.variableUpdates(substrateSelected);
        }

        this.editingOrderItemSubstrate = {
          catalogContainerSubstrateId: null,
          standardFillAmount: null,
          substrateAmount: null,
          substrateAmountUom: null,
          substrateQuantity: null
        };
        afterRender(() => this.emitSubstrateItem());
      }
    }
  }

  onSubstrateChange(event: any) {
    if (!isEmpty(event.value) && !isEmpty(this.selectedContainerId)) {
      const substrateSelected = (this.catalogInformation ?? [])
        .flatMap((ci) => ci?.Substrates ?? [])
        .find((sub) => isStringEqual(sub.SubstrateId, event.value) && isStringEqual(sub.ContainerId, this.selectedContainerId!));

      this.variableUpdates(substrateSelected);
      this.standardToggleDisabled = false;
    }

    this.standardToggleDisabled = false;
    afterRender(() => this.emitSubstrateItem());
  }

  private variableUpdates(substrateSelected: any) {
    this.substrateItem.substrateId = substrateSelected.SubstrateId;
    this.substrateItem.catalogContainerSubstrateId = substrateSelected.CatalogContainerSubstrateId;

    if (isEmpty(this.editingOrderItemSubstrate.catalogContainerSubstrateId)) {
      this.substrateItem.standard = true;
      this.substrateItem.customAmount =
        substrateSelected.StandardVolume === 0 || substrateSelected.StandardVolume === '0' ? null : substrateSelected.StandardVolume;
      this.substrateItem.customUnit = isStringEmpty(substrateSelected.StandardVolumeUom) ? null : substrateSelected.StandardVolumeUom;
    } else {
      this.substrateItem.standard = this.editingOrderItemSubstrate.standardFillAmount;
      this.substrateItem.customAmount =
        this.editingOrderItemSubstrate.substrateAmount === 0 || this.editingOrderItemSubstrate.substrateAmount === '0'
          ? null
          : this.editingOrderItemSubstrate.substrateAmount;
      this.substrateItem.customUnit = isStringEmpty(this.editingOrderItemSubstrate.substrateAmountUom)
        ? null
        : this.editingOrderItemSubstrate.substrateAmountUom;
    }
  }

  onStandardToggle(event: any): void {
    const substrateSelected = (this.catalogInformation ?? [])
      .flatMap((ci) => ci?.Substrates ?? [])
      .find((sub) => isStringEqual(sub.SubstrateId, this.substrateItem.substrateId!) && isStringEqual(sub.ContainerId, this.selectedContainerId!));

    if (event.checked) {
      this.substrateItem.customAmount =
        substrateSelected.StandardVolume === 0 || substrateSelected.StandardVolume === '0' ? null : substrateSelected.StandardVolume;
      this.substrateItem.customUnit = isStringEmpty(substrateSelected.StandardVolumeUom) ? null : substrateSelected.StandardVolumeUom;
    } else {
      if (isEmpty(this.editingOrderItemSubstrate.catalogContainerSubstrateId)) {
        this.substrateItem.customAmount =
          substrateSelected.StandardVolume === 0 || substrateSelected.StandardVolume === '0' ? null : substrateSelected.StandardVolume;
        this.substrateItem.customUnit = isStringEmpty(substrateSelected.StandardVolumeUom) ? null : substrateSelected.StandardVolumeUom;
      } else {
        this.substrateItem.customAmount =
          this.editingOrderItemSubstrate.substrateAmount === 0 || this.editingOrderItemSubstrate.substrateAmount === '0'
            ? null
            : this.editingOrderItemSubstrate.substrateAmount;
        this.substrateItem.customUnit = isStringEmpty(this.editingOrderItemSubstrate.substrateAmountUom)
          ? null
          : this.editingOrderItemSubstrate.substrateAmountUom;
      }
    }
    afterRender(() => this.emitSubstrateItem());
  }

  emitSubstrateItem() {
    this.substrateItemChangeEvent.emit(this.substrateItem);
  }

  private resetSubstrateItem() {
    this.substrateItem = {
      catalogContainerSubstrateId: null,
      substrateId: null,
      standard: true,
      customAmount: null,
      customUnit: null
    };
  }
}
